import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { 
  Box, CircularProgress, TextField, MenuItem, Checkbox, IconButton, Tooltip, Typography, createFilterOptions,
  FormControl, InputLabel, FormHelperText
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { useQuery } from '@tanstack/react-query';
import AuthContext from "./auth/Context";
import { useQuickAction } from './DataQuickActionContext';

import ManagedDropdown from "./ManagedDropdown";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function LookupFieldControl({
  field, value, onChange, error, isReadOnly, formData = {},masterData, formFields
}) {
  const context = useContext(AuthContext);
  const quickAction = useQuickAction();
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  // ================= 1. STATE QUẢN LÝ =================
  const [autoInputValue, setAutoInputValue] = useState('');
  const [autoSearchResults, setAutoSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCache, setSelectedCache] = useState([]); 
  const [isOpen, setIsOpen] = useState(false); 
  
  const searchTimeoutRef = useRef(null);

  // ================= 2. XỬ LÝ MAPPING & ĐIỀU KIỆN =================
  const valMap = field.apiMapping?.value || 'id';
  const apiLabelMap = field.apiMapping?.label || 'name'; 
  const formLabelMap = field.apiMapping?.label_as || apiLabelMap; 

  const fetchFieldsStr = useMemo(() => {
    if (!field.api) return '';
    const fieldsToFetch = [valMap, apiLabelMap];
    if (field.apiFields) fieldsToFetch.push(...field.apiFields.split(',').map(s => s.trim()));
    return [...new Set(fieldsToFetch)].join(','); 
  }, [field.api, valMap, apiLabelMap, field.apiFields]);

  // HIỆU NĂNG: chỉ field CÓ apiConditionFn mới phụ thuộc formData/masterData. Trong lưới chi tiết,
  // `masterData` đổi mỗi lần gõ phím ở BẤT KỲ đâu trên chứng từ; nếu để nó trong deps vô điều kiện thì
  // mọi ô autocomplete của mọi dòng đều tính lại điều kiện + JSON.stringify + chạy lại effect tìm kiếm.
  const hasConditionFn = typeof field.apiConditionFn === 'function';
  const finalApiCondition = useMemo(() => {
    let baseCondition = { ...(field.apiCondition || {}) };
    if (hasConditionFn) {
        baseCondition = field.apiConditionFn(formData, baseCondition, masterData||formData, formFields);
    }
    return baseCondition;
  }, [field.apiCondition, field.apiConditionFn, hasConditionFn, hasConditionFn ? formData : null, hasConditionFn ? masterData : null, formFields]);

  const conditionKey = JSON.stringify(finalApiCondition);
  // Effect tìm kiếm đọc điều kiện qua ref -> không cần để object vào deps (object mới mỗi render).
  const conditionRef = useRef(finalApiCondition);
  conditionRef.current = finalApiCondition;
  const apiName = field.api;
  
  const { data: resolvedApiCode, isLoading: isResolvingApi } = useQuery({
    queryKey: ['lookup_resolve_api_code', field.api],
    queryFn: async () => {
      const registryConfig = context.config?.getPageConfig?.(field.api);
      if (registryConfig?.apiCode && typeof(registryConfig?.apiCode)=="string" ) return registryConfig.apiCode;

      const res = await context.apis.asyncGetList(userToken, "listinfo", { condition: { code: field.api }, limit: 1, fields: "api_code,code" });
      const item = res?.data?.[0] || res?.[0];
      return item?.api_code || item?.code || field.api; 
    },
    enabled: !!field.api && !!userToken,
    staleTime: Infinity, 
  });

  // ================= 3. QUERIES DỮ LIỆU TĨNH & DỮ LIỆU ĐÃ CHỌN =================
  const { data: selectOptions, isLoading: isSelectLoading } = useQuery({
    queryKey: ['options_select', resolvedApiCode, conditionKey, fetchFieldsStr],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, resolvedApiCode, { condition: finalApiCondition, limit: 100, fields: fetchFieldsStr });
      return (res?.data || res || []).map(item => ({ value: item[valMap], label: item[apiLabelMap] || item[valMap], raw: item }));
    },
    enabled: field.type === 'Select' && !!resolvedApiCode && !!userToken,
    staleTime: 5 * 60 * 1000, 
  });

  const labelData = formData ? formData[formLabelMap] : null;

  const { dynamicLabelsFromForm, missingLabelValues } = useMemo(() => {
    const labels = [];
    const missing = [];
    if (field.type === 'Autocomplete' && value !== null && value !== undefined) {
        const valArray = Array.isArray(value) ? value : (value !== '' ? [value] : []);
        const labelArray = Array.isArray(labelData) ? labelData : (labelData !== null && labelData !== undefined ? [labelData] : []);

        valArray.forEach((v, idx) => {
            if (v === "") return; 
            if (selectedCache.find(c => String(c.value) === String(v))) return;
            if (field.options && field.options.find(o => String(o.value) === String(v))) return;
            
            const lbl = labelArray[idx];
            if (lbl !== undefined && lbl !== null) labels.push({ value: v, label: lbl });
            else missing.push(v);
        });
    }
    return { dynamicLabelsFromForm: labels, missingLabelValues: missing };
  }, [field.type, value, labelData, formLabelMap, selectedCache, field.options]);

  const { data: fetchedMissingLabels, isLoading: isLabelLoading } = useQuery({
    queryKey: ['options_auto_missing', resolvedApiCode, missingLabelValues.join('\u0001'), fetchFieldsStr],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, resolvedApiCode, { condition: { [valMap]: { $in: missingLabelValues } }, limit: missingLabelValues.length, fields: fetchFieldsStr });
      return (res?.data || res || []).map(item => ({ value: item[valMap], label: item[apiLabelMap] || item[valMap], raw: item }));
    },
    enabled: field.type === 'Autocomplete' && !!resolvedApiCode && missingLabelValues.length > 0 && !!userToken,
    staleTime: 10 * 60 * 1000, 
  });

  // ================= 4. QUERIES ĐỘNG =================
  const { data: defaultOptions, isLoading: isDefaultLoading } = useQuery({
    queryKey: ['options_auto_default', resolvedApiCode, conditionKey, fetchFieldsStr],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, resolvedApiCode, { condition: finalApiCondition, limit: 20, fields: fetchFieldsStr });
      return (res?.data || res || []).map(item => ({ value: item[valMap], label: item[apiLabelMap] || item[valMap], raw: item }));
    },
    enabled: isOpen && field.type === 'Autocomplete' && !!resolvedApiCode && !!userToken,
    staleTime: 5 * 60 * 1000, 
  });

  useEffect(() => {
    if (field.type !== 'Autocomplete' || !resolvedApiCode) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (autoInputValue.trim() !== '') {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
           const $or=[{[apiLabelMap]: { $regex: autoInputValue.trim(), $options: 'i' }}];
           // Tìm thêm theo các trường khác (vd SĐT, email) khi field khai `searchFields: 'dien_thoai,email'`.
           String(field.searchFields || '').split(',').map((f) => f.trim()).filter((f) => f && f !== apiLabelMap && f !== valMap)
             .forEach((f) => $or.push({ [f]: { $regex: autoInputValue.trim(), $options: 'i' } }));
           if(valMap !== "_id"){
            if(apiName === "account"){
                $or.push({[valMap]:{ $regex: '^' +  autoInputValue.trim(), $options: 'i' }})
            }else{
                $or.push({[valMap]:{ $regex: autoInputValue.trim(), $options: 'i' }})
            }
           }
           const searchCondition = { ...conditionRef.current, $or };
           const res = await context.apis.asyncGetList(userToken, resolvedApiCode, { condition: searchCondition, limit: 20, fields: fetchFieldsStr });
           const mappedResults = (res?.data || res || []).map(item => ({ value: item[valMap], label: item[apiLabelMap] || item[valMap], raw: item }));
           setAutoSearchResults(mappedResults);
        } catch (e) { console.error("Lỗi Autocomplete Search:", e); } 
        finally { setIsSearching(false); }
      }, 400); 
    } else {
      // Cập nhật theo hàm + trả về ĐÚNG tham chiếu cũ khi không có gì đổi -> React bỏ qua, không render lại.
      // (Trước đây gán thẳng `[]` = mảng MỚI mỗi lần -> mỗi lần effect chạy là thêm một lượt render.)
      setAutoSearchResults(prev => (prev.length ? [] : prev));
      setIsSearching(prev => (prev ? false : prev));
    }
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    // KHÔNG để `field` (object mới mỗi render ở lưới chi tiết) hay `finalApiCondition` (object) vào deps:
    // dùng `apiName` + `conditionKey` (chuỗi) + conditionRef để effect chỉ chạy khi ĐIỀU KIỆN thật sự đổi.
  }, [autoInputValue, apiName, resolvedApiCode, userToken, apiLabelMap, valMap, fetchFieldsStr, conditionKey, field.searchFields]);


  // ================= 5. TRỘN (MERGE) OPTIONS =================
  const currentOptions = useMemo(() => {
    let opts = field.options || [];
    if (field.type === 'Select' && selectOptions) opts = [...opts, ...selectOptions];
    if (field.type === 'Autocomplete') {
        const isTyping = autoInputValue.trim() !== '';
        opts = [
            ...opts, 
            ...dynamicLabelsFromForm, 
            ...selectedCache, 
            ...(fetchedMissingLabels || []), 
            ...(isTyping ? autoSearchResults : (defaultOptions || []))
        ].reduce((acc, curr) => {
            if (!acc.find(o => String(o.value) === String(curr.value))) acc.push(curr);
            return acc;
        }, []);
    }
    return opts;
  }, [field.options, field.type, selectOptions, dynamicLabelsFromForm, selectedCache, fetchedMissingLabels, defaultOptions, autoSearchResults, autoInputValue]);


  // ================= 6. THÊM NHANH (QUICK ADD) =================
  // Nhãn của ô có thể KHÔNG có: trang tuỳ biến hay dùng <LookupField field={{ type, api, apiMapping }} /> và tự vẽ nhãn bên ngoài.
  // Bản trước gọi thẳng `field.label.toLowerCase()` -> "Cannot read properties of undefined (reading 'toLowerCase')", cả trang sập
  // (lỗi thật 03/11/2026: trang Landing page, tab Cài đặt và nút chọn thêm sản phẩm).
  const tenField = String(field.label == null ? '' : field.label).trim();
  const handleQuickAddSuccess = (newRawItem) => {
      const newOption = { value: newRawItem[valMap], label: newRawItem[apiLabelMap] || newRawItem[valMap], raw: newRawItem };
      setSelectedCache(prev => {
          if (!prev.find(o => String(o.value) === String(newOption.value))) return [...prev, newOption];
          return prev;
      });
      if (field.multiple) {
          const currentVals = Array.isArray(value) ? value : [];
          const oldSelectedObjs = currentOptions.filter(opt => currentVals.includes(opt.value));
          onChange([...currentVals, newOption.value], [...oldSelectedObjs, newOption]);
      } else {
          onChange(newOption.value, newOption);
      }
  };

  const handleQuickAddClick = () => {
      if(!field.api) return context.alert(`Hệ thống chưa tìm thấy cấu trúc Form cho dữ liệu '${tenField || 'này'}'. Vui lòng bổ sung vào config file!`, null, "orange");
      let targetFields = field.quickAddFields;
      let quickAddPrefill = field.quickAddPrefill;
      if(!quickAddPrefill && finalApiCondition){
        quickAddPrefill = {}
        for(let key in finalApiCondition){
          if(typeof(finalApiCondition[key])!="object"){
            quickAddPrefill[key] = finalApiCondition[key];
          }
        }
      }
      quickAction.openAdd(field.api, targetFields, tenField ? `Thêm mới ${tenField}` : 'Thêm mới',quickAddPrefill||{} , handleQuickAddSuccess);
  };

  // ================= 7. RENDER =================
  const isLoadingOpts = isSelectLoading || isLabelLoading || isSearching || isDefaultLoading || isResolvingApi; 
  let helperTextStr = error || field.helpText || '';
  const isMultiple = !!field.multiple;
  const canQuickAdd = field.canQuickAdd===true || (field.canQuickAdd!=false && !!field.api); 

  const quickAddButton = (!isReadOnly && canQuickAdd ) ? (
      <Tooltip title={tenField ? `Thêm ${tenField.toLowerCase()} mới` : 'Thêm mới'}>
          <IconButton onClick={handleQuickAddClick} color="primary" sx={{ ml: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
              <AddCircleOutlineIcon />
          </IconButton>
      </Tooltip>
  ) : null;

  // --- RENDER SELECT ---
  if (field.type === 'Select') {
        const safeValue = isMultiple 
        ? (Array.isArray(value) ? value.map(v => String(v)) : (value !== null && value !== undefined && value !== '' ? [String(value)] : [])) 
        : (value !== null && value !== undefined ? String(value) : '');

        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <FormControl fullWidth size="small" error={!!error} disabled={isReadOnly}>
                <InputLabel shrink>{field.label}</InputLabel>
                
                <ManagedDropdown
                    multiple={isMultiple}
                    displayEmpty
                    value={safeValue}
                    label={field.label}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (isMultiple) {
                            const selectedOpts = currentOptions.filter(o => val.includes(String(o.value)));
                            onChange(selectedOpts.map(o => o.value), selectedOpts);
                        } else {
                            const selectedOpt = currentOptions.find(o => String(o.value) === String(val));
                            onChange(selectedOpt ? selectedOpt.value : val, selectedOpt || { value: val, label: val === '' ? '-- Vui lòng chọn --' : val, raw: null });
                        }
                    }}
                    startAdornment={isLoadingOpts ? <CircularProgress size={16} sx={{mr:1}}/> : null}
                    sx={{ borderRadius: '8px', bgcolor: isReadOnly ? 'action.hover' : 'transparent' }}
                >
                    {currentOptions.length === 0 && !isLoadingOpts && <MenuItem disabled>Không có dữ liệu</MenuItem>}
                    
                    {!isMultiple && !currentOptions.find(o => String(o.value) === "") && (
                    <MenuItem value=""><em>-- Vui lòng chọn --</em></MenuItem>
                    )}

                    {currentOptions.map((opt, idx) => (
                    <MenuItem key={`${opt.value}_${idx}`} value={String(opt.value)}>
                        {isMultiple && <Checkbox checked={safeValue.indexOf(String(opt.value)) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />}
                        {opt.label}
                    </MenuItem>
                    ))}
                </ManagedDropdown>
                
                {helperTextStr && <FormHelperText>{helperTextStr}</FormHelperText>}
                </FormControl>
                
                {quickAddButton}
            </Box>
        );
  }

  // --- RENDER AUTOCOMPLETE ---
  let selectedOption = isMultiple ? [] : null;
  if (isMultiple) {
    const valArray = Array.isArray(value) ? value : (value !== null && value !== undefined && value !== '' ? [value] : []);
    selectedOption = currentOptions.filter(opt => valArray.some(v => String(v) === String(opt.value)));
  } else {
    selectedOption = (value !== null && value !== undefined) 
       ? currentOptions.find(opt => String(opt.value) === String(value)) || null 
       : null;
  }

  const isAccountApi = field.api === 'account';

  // FIX 1: Chống crash cho customFilter bằng Optional Chaining (?.)
  const customFilter = createFilterOptions({ 
    matchFrom: 'any', 
    stringify: (option) => `${option?.label || ''} ${option?.value || ''}` 
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <ManagedDropdown
        type="autocomplete" 
        multiple={isMultiple} 
        disableCloseOnSelect={isMultiple} 
        readOnly={isReadOnly} 
        options={currentOptions} 
        filterOptions={customFilter}
        
        // FIX 2: Bọc String() kết hợp ?. chống văng lỗi nếu thuộc tính bị rỗng
        isOptionEqualToValue={(option, val) => String(option?.value) === String(val?.value)}

        getOptionLabel={(opt) => {
           if (!opt) return '';
           if (isAccountApi) return String(opt?.value || '');
           return opt?.label !== undefined && opt?.label !== null ? String(opt.label) : String(opt?.value || '');
        }}

        value={selectedOption} 
        loading={isLoadingOpts} 
        sx={{ flexGrow: 1 }}
        
        onOpen={() => setIsOpen(true)}
        
        // FIX 3: Khi đóng dropdown ở chế độ Multiple, mới clear chuỗi tìm kiếm
        onClose={() => {
            setIsOpen(false);
            if (isMultiple) {
                setAutoInputValue(''); 
            }
        }}
        
        // FIX 4: Ép MUI giữ nguyên Text đang gõ (nếu là chế độ Multiple)
        {...(isMultiple ? { inputValue: autoInputValue } : {})}

        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') setAutoInputValue(newInputValue);
          else if (reason === 'clear') setAutoInputValue('');
          
          // Nòng cốt Fix 4: Ở chế độ multiple, chọn 1 mục sẽ kích hoạt "reset". 
          // Chúng ta CHẶN không clear search text, để người dùng tiếp tục nhìn thấy kết quả và chọn mục thứ 2, 3...
          else if (reason === 'reset' && !isMultiple) setAutoInputValue('');
        }}

        onChange={(e, newValue) => {
          if (isMultiple) {
             const newItems = newValue || [];
             setSelectedCache(prev => {
                return [...prev, ...newItems].reduce((acc, curr) => {
                    if (!acc.find(o => String(o.value) === String(curr.value))) acc.push(curr);
                    return acc;
                }, []);
             });
             onChange(newItems.map(v => v.value), newItems); 
          } else {
             if (newValue) {
                 setSelectedCache(prev => {
                     if (!prev.find(o => String(o.value) === String(newValue.value))) return [...prev, newValue];
                     return prev;
                 });
             }
             onChange(newValue !== null && newValue !== undefined ? newValue.value : '', newValue || { value: '', label: '', raw: null });
          }
        }} 

        renderOption={(props, option, { selected }) => { 
          const { key, ...optionProps } = props; 
          return (
            <li key={key || option.value} {...optionProps} style={{ alignItems: 'flex-start', paddingTop: 6, paddingBottom: 6 }}>
              {isMultiple && (
                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8, marginTop: -2, padding: 4 }} checked={selected} />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                 <Typography variant="body2" sx={{ fontWeight: '500', color: 'text.primary', lineHeight: 1.3 }}>
                    {option.label}
                 </Typography>
                 {String(option.value) !== String(option.label) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2, fontSize: '0.75rem' }}>
                       {isAccountApi ? `TK: ${option.value}` : `Mã: ${option.value}`}
                    </Typography>
                 )}
              </Box>
            </li>
          ); 
        }}
        renderInput={(params) => (<TextField {...params} label={field.label} required={field.required} error={!!error} helperText={helperTextStr} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: isReadOnly ? 'action.hover' : 'transparent' } }} InputProps={{ ...params.InputProps, endAdornment: (<React.Fragment>{isLoadingOpts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</React.Fragment>) }}/>)} 
      />
      {quickAddButton}
    </Box>
  );
}